<template>
  <Modal
    :show="isVisible"
    :title="title"
    :size="size"
    :theme="theme"
    :show-close-button="false"
    :close-on-escape="false"
    :show-default-footer="true"
    :show-cancel-button="true"
    :show-confirm-button="true"
    :cancel-button-text="cancelText"
    :confirm-button-text="confirmText"
    :confirm-button-variant="confirmButtonVariant"
    cancel-button-id="confirm-action-cancel-button"
    confirm-button-id="confirm-action-confirm-button"
    @cancel="handleCancel"
    @confirm="handleConfirm"
  >
    <div class="space-y-4">
      <div v-if="icon" class="flex justify-center">
        <div :class="iconContainerClasses">
          <component :is="icon" :class="iconClasses" />
        </div>
      </div>
      
      <div class="text-center">
        <p v-if="message" class="text-gray-700 whitespace-pre-line">{{ message }}</p>
        <slot v-else></slot>
      </div>
      
      <div v-if="showLoading" class="flex justify-center">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    </div>
  </Modal>
</template>

<script setup>
defineOptions({
  name: 'ConfirmAction',
});

import { computed } from 'vue'
import Modal from './Modal.vue'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle
} from '@lucide/vue'

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Bevestig Actie'
  },
  message: {
    type: String,
    default: ''
  },
  confirmText: {
    type: String,
    default: 'Bevestigen'
  },
  cancelText: {
    type: String,
    default: 'Annuleren'
  },
  type: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'danger', 'warning', 'info', 'success'].includes(value)
  },
  size: {
    type: String,
    default: 'sm'
  },
  showLoading: {
    type: Boolean,
    default: false
  },
  theme: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['confirm', 'cancel'])

// Computed properties for styling based on type
const theme = computed(() => {
  if (props.theme) return props.theme
  if (props.type === 'danger' || props.type === 'warning') return 'orange'
  return 'default'
})

const confirmButtonVariant = computed(() => {
  return props.type
})

const icon = computed(() => {
  const icons = {
    danger: AlertTriangle,
    warning: AlertCircle,
    info: Info,
    success: CheckCircle,
    primary: null
  }
  return icons[props.type]
})

const iconContainerClasses = computed(() => {
  const classes = {
    danger: 'bg-red-100',
    warning: 'bg-yellow-100',
    info: 'bg-blue-100',
    success: 'bg-green-100',
    primary: 'bg-gray-100'
  }
  return `${classes[props.type]} rounded-full p-3`
})

const iconClasses = computed(() => {
  const classes = {
    danger: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
    success: 'text-green-600',
    primary: 'text-gray-600'
  }
  return `${classes[props.type]} w-6 h-6`
})

// Event handlers
const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
}
</script>